/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React, { useState, useEffect } from 'react';
import { useDeleteDataIntegrationConnectionMutation, usePauseDataIntegrationConnectionMutation, useResumeDataIntegrationConnectionMutation, useSyncDataIntegrationConnectionMutation, useResyncDataIntegrationConnectionMutation, useTestDataIntegrationConnectionMutation, useListDataIntegrationConnectionsQuery } from '../../../../../../api/initiative-data-integrations';
import { BasicAlert } from '@g17eco/molecules/alert';
import { DataConnectionCard } from './DataConnectionCard';
import { useSiteAlert } from '../../../../../../hooks/useSiteAlert';
import { SiteAlertColors } from '../../../../../../slice/siteAlertsSlice';
import { BlockingLoader } from '../../../../../../atoms/loader';
import { PreviewAlert } from '@g17eco/molecules/feature-stability/PreviewAlert';

interface DataConnectionsProps {
  initiativeId: string;
  handleViewSchemas: (connectionId: string, connectionName: string) => void;
}

export const DataConnections: React.FC<DataConnectionsProps> = ({
  initiativeId,
  handleViewSchemas,
}) => {
  // Track last sync action per connection
  const [lastSyncAction, setLastSyncAction] = useState<{ [connectionId: string]: 'sync' | 'resync' | null }>({});
  const [testingConnectionId, setTestingConnectionId] = useState<string | null>(null);

  const { addSiteAlert, addSiteError } = useSiteAlert();

  const [deleteConnection] = useDeleteDataIntegrationConnectionMutation();
  const [pauseConnection] = usePauseDataIntegrationConnectionMutation();
  const [resumeConnection] = useResumeDataIntegrationConnectionMutation();
  const [syncConnection] = useSyncDataIntegrationConnectionMutation();
  const [resyncConnection] = useResyncDataIntegrationConnectionMutation();
  const [testConnection] = useTestDataIntegrationConnectionMutation();

    const {
    data: connections,
    isLoading: connectionsLoading,
  } = useListDataIntegrationConnectionsQuery(
    { initiativeId },
    {
      // Only poll when on connections tab and not viewing details
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );

  // Clear lastSyncAction when connection leaves syncing state
  useEffect(() => {
    setLastSyncAction((prev) => {
      const updated = { ...prev };
      let hasChanges = false;

      const connectionSyncStates = connections?.map(conn => ({ id: conn.id, syncState: conn.status.sync_state })) ?? []
      connectionSyncStates.forEach((conn) => {
        if (conn.syncState !== 'syncing' && prev[conn.id]) {
          updated[conn.id] = null;
          hasChanges = true;
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [connections]);

  const handleDeleteConnection = async (connectionId: string, connectionName: string) => {
    if (!confirm(`Are you sure you want to delete '${connectionName}'?`)) {
      return;
    }

    try {
      await deleteConnection({ initiativeId, connectionId });
      addSiteAlert({
        content: `Data connection '${connectionName} (${connectionId})' deleted successfully`,
        color: SiteAlertColors.Secondary,
      });
    } catch {
      addSiteError({ content: 'Failed to delete data connection' });
    }
  };

  const handlePauseResumeConnection = async (connectionId: string, connectionName: string, isPaused: boolean) => {
    try {
      if (isPaused) {
        await resumeConnection({ initiativeId, connectionId }).unwrap();
        addSiteAlert({
          content: `Data connection '${connectionName}' resumed successfully`,
          color: SiteAlertColors.Success,
        });
      } else {
        await pauseConnection({ initiativeId, connectionId }).unwrap();
        addSiteAlert({
          content: `Data connection '${connectionName}' paused successfully`,
          color: SiteAlertColors.Success,
        });
      }
    } catch {
      const action = isPaused ? 'resume' : 'pause';
      addSiteError({ content: `Failed to ${action} data connection '${connectionName}'` });
    }
  };

  const handleSyncConnection = async (connectionId: string, connectionName: string) => {
    try {
    setLastSyncAction((prev) => ({ ...prev, [connectionId]: 'sync' }));
    await syncConnection({ initiativeId, connectionId }).unwrap();
      addSiteAlert({
        content: `Data connection '${connectionName}' sync initiated successfully`,
        color: SiteAlertColors.Success,
      });
    } catch {
      addSiteError({ content: `Failed to sync data connection '${connectionName}'` });
    }
  };

  const handleResyncConnection = async (connectionId: string, connectionName: string) => {
    if (
      confirm(
        `Are you sure you want to trigger a full historical re-sync for '${connectionName}'? This may take a while and could impact your usage quota.`,
      )
    ) {
      setLastSyncAction((prev) => ({ ...prev, [connectionId]: 'resync' }));
      try {
        await resyncConnection({ initiativeId, connectionId }).unwrap();
        addSiteAlert({
          content: `Data connection '${connectionName}' resync initiated successfully`,
          color: SiteAlertColors.Success,
        });
      } catch {
        addSiteError({ content: `Failed to resync data connection '${connectionName}'` });
      }
    }
  };

  const handleTestConnection = async (connectionId: string, connectionName: string) => {
    try {
      setTestingConnectionId(connectionId);
      await testConnection({ initiativeId, connectionId }).unwrap();
      addSiteAlert({
        content: `Data connection '${connectionName}' test completed successfully`,
        color: SiteAlertColors.Success,
      });
    } catch {
      addSiteAlert({ content: `Failed to test data connection '${connectionName}'`, color: SiteAlertColors.Danger });
    } finally {
      setTestingConnectionId(null);
    }
  };

  if (connectionsLoading) {
    return <BlockingLoader />;
  }

  if (connections?.length === 0) {
    return (
      <BasicAlert type='info' className='text-center'>
        <i className='fal fa-info-circle me-2' />
        No data connections configured yet. Switch to the 'Add Connection' tab to get started.
      </BasicAlert>
    );
  }

  return (
    <>
      <PreviewAlert />
      {/* Connection Status Info */}
      <div className='mb-3 p-3 bg-light rounded'>
        <div className='d-flex align-items-center'>
          <i className='fal fa-database text-muted me-2' />
          <small className='text-muted'>
            <strong>{connections?.length}</strong> data connection{connections?.length !== 1 ? 's' : ''} configured.
          </small>
        </div>
      </div>

      <div className='data-connections-list'>
        {connections?.map((connection) => (
        <DataConnectionCard
          key={connection.id}
          connection={connection}
          lastSyncAction={lastSyncAction[connection.id]}
          testingConnectionId={testingConnectionId}
          onDeleteConnection={handleDeleteConnection}
          onPauseResumeConnection={handlePauseResumeConnection}
          onSyncConnection={handleSyncConnection}
          onResyncConnection={handleResyncConnection}
          onTestConnection={handleTestConnection}
          onViewSchemas={handleViewSchemas}
        />
      ))}
      </div>
    </>
  );
};
